import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  useSearchParams,
  useNavigate,
  Link,
  useParams,
} from "react-router-dom";
import { IGetProductsParams, productApi } from "../services/api/productApi";
import { categoryApi } from "../services/api/categoryApi";
import { styleApi, IStyle } from "../services/api/styleApi";
import { materialApi, IMaterial } from "../services/api/materialApi";
import { colorApi, IColor } from "../services/api/colorApi";
import { artistApi, IArtist } from "../services/api/artistApi";
import { cartApi } from "../services/api/cartApi";
import { ordersApi } from "../services/api/orderApi";
import { ICategory } from "../types";
import Loader from "../components/Loader";
import { wishlistApi } from "../services/api/wishlistApi";
import { showError, showSuccess } from "../lib/toast";
import { notifyWishlistUpdate } from "../utils/wishlistUtils";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "react-toastify";
import Carousel from "react-multi-carousel";
import { notifyCartUpdate } from "../utils/cartUpdateUtils";
import ImageWithLoader from "../components/ImageWithLoader";

type CollectionType = "Canvas" | "DigiVision";

// Utility function to shuffle array randomly
const shuffleArray = <T,>(array: T[]): T[] => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const Products: React.FC = () => {
  const navigate = useNavigate();
  const { isLoggedIn, refreshUserData } = useAuth();
  const [searchParams] = useSearchParams();
  const { categoryName } = useParams<{ categoryName?: string }>();

  const [products, setProducts] = useState<any[]>([]);
  const [wishlist, setWishlist] = useState<string[]>([]);
  const [categories, setCategories] = useState<ICategory[]>([]);
  const [styles, setStyles] = useState<IStyle[]>([]);
  const [materials, setMaterials] = useState<IMaterial[]>([]);
  const [colors, setColors] = useState<IColor[]>([]);
  const [artists, setArtists] = useState<IArtist[]>([]);
  const [loadingFilters, setLoadingFilters] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loadingCategory, setLoadingCategory] = useState(true);
  const [loadingFeatured, setLoadingFeatured] = useState(true);
  const [addingToCart, setAddingToCart] = useState<string | null>(null);
  const [ownedProducts, setOwnedProducts] = useState<Set<string>>(new Set());
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [activeTab, setActiveTab] = useState<string>("");

  // Dynamic tabs/filters for Digi Cartt Picks - get from Canvas category subcategories
  const staticTabs = React.useMemo(() => {
    // Find Canvas category
    const canvasCategory = categories.find(
      (category) => category.name.split(" ")[0].toLowerCase() === "canvas",
    );

    // Get subcategory names from Canvas category
    const canvasSubcategories =
      canvasCategory?.subCategories?.map((sub) => sub.name) || [];

    // Ensure we always have Canvas category available
    // If no subcategories found, return empty array or default tabs
    return canvasSubcategories.length > 0 ? canvasSubcategories : [];
  }, [categories]);

  const [featuredProducts, setFeaturedProducts] = useState<any[]>([]);

  const [wishlistLoading, setWishlistLoading] = useState<{
    [key: string]: boolean;
  }>({});
  const [activeCollection, setActiveCollection] =
    useState<CollectionType | null>(null);

  // Set initial activeTab when staticTabs are loaded
  useEffect(() => {
    if (staticTabs.length > 0 && !activeTab && activeCollection === "Canvas") {
      setActiveTab(staticTabs[0]);
    }
  }, [staticTabs, activeTab, activeCollection]);
  const [filteredSubcategories, setFilteredSubcategories] = useState<string[]>(
    [],
  );

  const [filters, setFilters] = useState<IGetProductsParams>({
    page: 1,
    limit: 12,
    sortBy: "createdAt",
    sortOrder: "desc",
  });
  const [selectedFilters, setSelectedFilters] = useState<{
    [key: string]: string[];
  }>({
    categories: [],
    subCategories: [],
    styles: [],
    materials: [],
    colors: [],
    artists: [],
    priceRanges: [],
  });
  const digiPacksCarouselRef = useRef<any>(null);
  const subcategoriesCarouselRef = useRef<any>(null);
  const isInitialMount = useRef(true);
  const isInitialFeaturedFetch = useRef(true);
  const isFetchingProducts = useRef(false);
  const isFetchingFeatured = useRef(false);
  const [collectionProcessed, setCollectionProcessed] = useState(false);
  const [categoryFilterProcessed, setCategoryFilterProcessed] = useState(false);
  const prevCollectionRef = useRef<string | null>(null);

  const collection = searchParams.get("collection");
  const categoryParam = searchParams.get("category");

  // Filter subcategories when activeCollection or categories change
  useEffect(() => {
    if (activeCollection && categories.length > 0) {
      const matchedCategory = categories.find(
        (category) =>
          category.name.split(" ")[0].toLowerCase() ===
          activeCollection.toLowerCase(),
      );
      if (matchedCategory) {
        // Extract subcategory names from the new structure
        const subCategoryNames = matchedCategory.subCategories.map(
          (sub) => sub.name,
        );
        setFilteredSubcategories(subCategoryNames || []);
      } else {
        setFilteredSubcategories([]);
      }
    } else {
      setFilteredSubcategories([]);
    }
  }, [activeCollection, categories]);

  // Handle category filter from query parameter
  useEffect(() => {
    // Reset category filter processed state when categoryParam changes
    setCategoryFilterProcessed(false);

    if (categoryParam && categories.length > 0 && activeCollection) {
      const decodedCategory = decodeURIComponent(categoryParam);
      // Find the active collection category
      const matchedCategory = categories.find(
        (category) =>
          category.name.split(" ")[0].toLowerCase() ===
          activeCollection.toLowerCase(),
      );

      // Check if this category exists in the matched category's subcategories
      const categoryExists = matchedCategory?.subCategories.some(
        (sub) => sub.name === decodedCategory,
      );

      if (categoryExists) {
        setSelectedFilters((prev) => ({
          ...prev,
          subCategories: [decodedCategory],
        }));
        setCategoryFilterProcessed(true);
      } else {
        // Category doesn't exist, mark as processed anyway
        setCategoryFilterProcessed(true);
      }
    } else if (!categoryParam) {
      // No category param, mark as processed
      setCategoryFilterProcessed(true);
    }
  }, [categoryParam, categories, activeCollection]);

  useEffect(() => {
    // Only process if collection actually changed
    if (prevCollectionRef.current === collection) {
      return;
    }
    
    const checkDigiVisionAccess = async () => {
      if (collection && ["Canvas", "DigiVision"].includes(collection)) {
        // Check CosmoSlots KYC verification for DigiVision collection
        if (collection === "DigiVision") {
          if (!isLoggedIn) {
            showError("Please login to view this category");
            navigate("/login?isDigivision=true");
            prevCollectionRef.current = collection;
            setCollectionProcessed(true); // Set processed even on redirect to prevent infinite loops
            return;
          }

          // Refresh user data to get latest KYC status before checking
          if (isLoggedIn) {
            try {
              await refreshUserData();
              // Get updated user data after refresh
              const updatedUserStr = localStorage.getItem('user');
              const updatedUser = updatedUserStr ? JSON.parse(updatedUserStr) : null;
              
              if (!updatedUser || !updatedUser.kycVerified) {
                showError(
                  "Please wait for your KYC verification to complete before accessing this category",
                );
                navigate("/");
                prevCollectionRef.current = collection;
                setCollectionProcessed(true); // Set processed even on redirect to prevent infinite loops
                return;
              }
            } catch (error) {
              console.error('Error refreshing user data:', error);
              // If refresh fails, check with current user data
              const currentUserStr = localStorage.getItem('user');
              const currentUser = currentUserStr ? JSON.parse(currentUserStr) : null;
              if (currentUser && !currentUser.kycVerified) {
                showError(
                  "Please wait for your KYC verification to complete before accessing this category",
                );
                navigate("/");
                prevCollectionRef.current = collection;
                setCollectionProcessed(true); // Set processed even on redirect to prevent infinite loops
                return;
              }
            }
          }
        }

        setActiveCollection(collection as CollectionType);
        // Only reset page if collection actually changed
        if (prevCollectionRef.current !== collection) {
          setFilters((prev) => ({
            ...prev,
            // category: collection,
            page: 1, // Reset to first page only when collection changes
          }));
        }
        prevCollectionRef.current = collection;
        setCollectionProcessed(true);
      } else {
        // No collection specified, clear activeCollection
        if (prevCollectionRef.current !== collection) {
          setActiveCollection(null);
          prevCollectionRef.current = collection;
        }
        setCollectionProcessed(true);
      }
    };

    checkDigiVisionAccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collection, isLoggedIn]); // Removed refreshUserData and user from deps to prevent re-runs

  // Handle category filtering from URL parameter
  useEffect(() => {
    if (categoryName && categories.length > 0) {
      // Find the category by name
      const category = categories.find((cat) =>
        cat.subCategories.some((item) => item.name === categoryName),
      );

      const subcategory: any = category?.subCategories.find(
        (item) => item.name === categoryName,
      );
      setActiveTab(subcategory.name);
      if (subcategory) {
        setFilters((prev) => ({
          ...prev,
          categoryId: subcategory._id,
          page: 1, // Reset to first page
        }));
      } else {
        // Category not found, show error or redirect
        console.error(`Category "${categoryName}" not found`);
        navigate("/products?collection=Canvas");
      }
    }
  }, [categoryName, categories, navigate]);

  const fetchWishlist = useCallback(async () => {
    // Only fetch wishlist if user is logged in
    if (!isLoggedIn) {
      setWishlist([]);
      return;
    }

    try {
      const wishlistResponse: any = await wishlistApi.getWishlist();
      setWishlist(
        wishlistResponse?.items?.map((item: any) => item?.product?._id) || [],
      );
    } catch (error) {
      console.error("Error fetching wishlist:", error);
      setWishlist([]);
    }
  }, [isLoggedIn]);

  const checkOwnedProducts = useCallback(
    async (productIds: string[]) => {
      if (!isLoggedIn || productIds.length === 0) return;

      try {
        const response: any =
          await ordersApi.checkMultipleProductsOwnership(productIds);
        if (response.success && response.data?.ownershipMap) {
          const owned = new Set<string>();
          Object.entries(response.data.ownershipMap).forEach(
            ([productId, isOwned]) => {
              if (isOwned) owned.add(productId);
            },
          );
          setOwnedProducts(owned);
        }
      } catch (error) {
        console.error("Error checking owned products:", error);
      }
    },
    [isLoggedIn],
  );

  const convertInchesToCm = (size: string): string => {
    const numbers = size.replace("inches", "").trim().split("x");
    if (numbers.length !== 2) return size; // fallback

    const widthInCm = (parseFloat(numbers[0].trim()) * 2.54).toFixed(1);
    const heightInCm = (parseFloat(numbers[1].trim()) * 2.54).toFixed(1);

    return `${widthInCm} x ${heightInCm} cm`;
  };
  const fetchProducts = useCallback(
    async (isLoadMore = false) => {
      // Prevent overlapping calls
      if (isFetchingProducts.current && !isLoadMore) {
        console.log("[Products] Already fetching products, skipping...");
        return;
      }

      try {
        if (!isLoadMore) {
          setLoading(true);
          isFetchingProducts.current = true;
        }

        // Prepare the request parameters
        const requestParams: IGetProductsParams = {
          ...filters,
          page: filters.page || 1,
          limit: filters.limit || 12,
          // Explicitly include sortBy and sortOrder
          sortBy: filters.sortBy || "createdAt",
          sortOrder: filters.sortOrder || "desc",
        };

        // Add collection filter if activeCollection is set
        if (activeCollection) {
          requestParams.collection = activeCollection;
        }

        // Apply selected filters
        if (selectedFilters.subCategories?.length > 0) {
          // Send all selected subcategories as comma-separated string
          requestParams.subCategories = selectedFilters.subCategories.join(",");
        }

        if (selectedFilters.styles?.length > 0) {
          // Send all selected styles as comma-separated IDs
          const styleIds = selectedFilters.styles
            .map((styleName) => {
              const style = styles.find((s) => s.name === styleName);
              return style?._id;
            })
            .filter((id) => id !== undefined);
          if (styleIds.length > 0) {
            requestParams.styleId = styleIds.join(",");
          }
        }

        if (selectedFilters.materials?.length > 0) {
          // Send all selected materials as comma-separated IDs
          const materialIds = selectedFilters.materials
            .map((materialName) => {
              const material = materials.find((m) => m.name === materialName);
              return material?._id;
            })
            .filter((id) => id !== undefined);
          if (materialIds.length > 0) {
            requestParams.materialId = materialIds.join(",");
          }
        }

        if (selectedFilters.colors?.length > 0) {
          // Send all selected colors as comma-separated IDs
          const colorIds = selectedFilters.colors
            .map((colorName) => {
              const color = colors.find((c) => c.name === colorName);
              return color?._id;
            })
            .filter((id) => id !== undefined);
          if (colorIds.length > 0) {
            requestParams.colorId = colorIds.join(",");
          }
        }

        if (selectedFilters.artists?.length > 0) {
          // Send all selected artists as comma-separated IDs
          const artistIds = selectedFilters.artists
            .map((artistName) => {
              const artist = artists.find((a) => a.name === artistName);
              return artist?._id;
            })
            .filter((id) => id !== undefined);
          if (artistIds.length > 0) {
            requestParams.artistId = artistIds.join(",");
          }
        }

        if (selectedFilters.priceRanges?.length > 0) {
          // Send all selected price ranges as comma-separated string
          requestParams.priceRange = selectedFilters.priceRanges.join(",");
        }

        // Clean up undefined values (but keep collection, sortBy, and sortOrder parameters)
        const cleanFilters = Object.fromEntries(
          Object.entries(requestParams).filter(([key, v]) => {
            // Always keep these parameters if they exist
            if (
              (key === "collection" ||
                key === "sortBy" ||
                key === "sortOrder") &&
              v !== undefined
            )
              return true;
            return v !== undefined && v !== "";
          }),
        ) as IGetProductsParams;

        console.log("[Products] Fetching products with params:", cleanFilters);
        console.log("[Products] Active collection:", activeCollection);
        console.log(
          "[Products] Sort by:",
          cleanFilters.sortBy,
          "Order:",
          cleanFilters.sortOrder,
        );

        const response = await productApi.getProducts(cleanFilters);

        // Shuffle products to show in random order
        const shuffledProducts = shuffleArray(response.data.products);

        if (isLoadMore) {
          // Append new products to existing ones
          setProducts((prev) => [...prev, ...shuffledProducts]);
        } else {
          // Replace products for new search/filter
          setProducts(shuffledProducts);
        }

        // Update hasMore based on response
        setHasMore(response.data.products.length === (filters.limit || 12));

        // Check ownership for fetched products
        if (isLoggedIn && response.data.products?.length > 0) {
          const productIds = response.data.products.map((p: any) => p._id);
          await checkOwnedProducts(productIds);
        }
      } catch (error) {
        console.error("Error fetching products:", error);
      } finally {
        setLoading(false);
        setLoadingMore(false);
        isFetchingProducts.current = false;
      }
    },
    [
      filters,
      selectedFilters,
      styles,
      materials,
      colors,
      artists,
      isLoggedIn,
      checkOwnedProducts,
      activeCollection,
    ],
  );

  const loadMoreProducts = useCallback(async () => {
    if (loadingMore || !hasMore) return;

    setLoadingMore(true);
    setFilters((prev) => ({
      ...prev,
      page: (prev.page || 1) + 1,
    }));
  }, [loadingMore, hasMore]);

  // Handle static tab click - ONLY affects Digi Cartt Picks section
  const handleTabClick = (tabName: string) => {
    setActiveTab(tabName);
    // Trigger featured products fetch with the new tab filter
    // Pass tabName directly to avoid stale state issue
    // This ONLY affects the Digi Cartt Picks carousel, NOT the main product grid
    fetchFeaturedProducts(tabName);
  };

  const fetchCategories = async () => {
    try {
      const response = await categoryApi.getCategories();
      console.log("response", response);
      setCategories(response.data);
    } catch (error) {
      console.error("Error fetching categories:", error);
    } finally {
      setLoadingCategory(false);
    }
  };

  const fetchStyles = async () => {
    try {
      const response = await styleApi.getStyles();
      setStyles(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error("Error fetching styles:", error);
      setStyles([]);
    }
  };

  const fetchMaterials = async () => {
    try {
      const response = await materialApi.getMaterials();
      setMaterials(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error("Error fetching materials:", error);
      setMaterials([]);
    }
  };

  const fetchColors = async () => {
    try {
      const response = await colorApi.getColors();
      setColors(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error("Error fetching colors:", error);
      setColors([]);
    }
  };

  const fetchArtists = async () => {
    try {
      const response = await artistApi.getArtists();
      setArtists(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error("Error fetching artists:", error);
      setArtists([]);
    }
  };

  useEffect(() => {
    const loadAllFilters = async () => {
      setLoadingFilters(true);
      try {
        await Promise.all([
          fetchCategories(),
          fetchStyles(),
          fetchMaterials(),
          fetchColors(),
          fetchArtists(),
        ]);
      } catch (error) {
        console.error("Error loading filters:", error);
      } finally {
        setLoadingFilters(false);
      }
    };

    loadAllFilters();
  }, []);

  useEffect(() => {
    fetchWishlist();
  }, [fetchWishlist]);

  // Track previous URL params to detect changes
  const prevCategoryParam = useRef<string | null>(null);
  const prevCollection = useRef<string | null>(null);
  const isProcessingUrlChange = useRef(false);

  // Initial fetch on mount - wait for activeCollection and category filter to be determined from URL
  useEffect(() => {
    // Check if URL params changed (for navigation between categories)
    const urlParamsChanged =
      prevCategoryParam.current !== categoryParam ||
      prevCollection.current !== collection;

    // Only fetch once on initial mount OR when URL params change
    // Wait for collection to be processed AND category filter to be processed (if categoryParam exists)
    const shouldWaitForCategory = categoryParam !== null;
    const canFetch =
      collectionProcessed &&
      (!shouldWaitForCategory || categoryFilterProcessed);

    if ((isInitialMount.current || urlParamsChanged) && canFetch) {
      isProcessingUrlChange.current = true;
      console.log(
        "[Products] Fetch triggered - activeCollection:",
        activeCollection,
        "categoryFilter:",
        categoryParam,
      );
      // Reset page to 1 when URL changes
      setFilters((prev) => ({ ...prev, page: 1 }));
      fetchProducts();
      isInitialMount.current = false;
      prevCategoryParam.current = categoryParam;
      prevCollection.current = collection;
      // Reset flag after a short delay to allow other useEffects to skip
      setTimeout(() => {
        isProcessingUrlChange.current = false;
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collectionProcessed, categoryFilterProcessed, categoryParam, collection]); // Wait for both collection and category filter to be processed

  // Fetch products when filters or selectedFilters change (after initial mount)
  useEffect(() => {
    // Skip initial mount (already handled above)
    // Also skip if URL params just changed (handled by URL change useEffect)
    if (!isInitialMount.current && !isProcessingUrlChange.current) {
      // Only fetch if page is 1 (to avoid double fetch when page changes)
      // Page changes > 1 are handled separately in the loadMore useEffect
      if (filters.page === 1) {
        console.log("[Products] Filter change fetch triggered");
        fetchProducts();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedFilters,
    filters.sortBy,
    filters.sortOrder,
    filters.categoryId,
    filters.page,
  ]); // Removed activeCollection to prevent duplicate calls

  // Handle load more when page changes
  useEffect(() => {
    if (filters.page && filters.page > 1) {
      fetchProducts(true); // isLoadMore = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.page]);
  const isProductInWishlist = (productId: string) => {
    return wishlist.some((item) => item === productId);
  };

  // Filter handling functions
  const handleFilterChange = (
    filterType: string,
    value: string,
    checked: boolean,
  ) => {
    setSelectedFilters((prev) => {
      const currentValues = prev[filterType] || [];
      let newValues;

      if (checked) {
        newValues = [...currentValues, value];
      } else {
        newValues = currentValues.filter((v) => v !== value);
      }

      return {
        ...prev,
        [filterType]: newValues,
      };
    });

    // Reset to first page when filters change
    setFilters((prev) => ({
      ...prev,
      page: 1,
    }));
    setHasMore(true); // Reset hasMore when filters change
  };

  const handlePriceRangeChange = (range: string, checked: boolean) => {
    handleFilterChange("priceRanges", range, checked);
  };

  const clearAllFilters = () => {
    setSelectedFilters({
      categories: [],
      subCategories: [],
      styles: [],
      materials: [],
      colors: [],
      artists: [],
      priceRanges: [],
    });
    setFilters((prev) => ({
      ...prev,
      page: 1,
    }));
    setHasMore(true); // Reset hasMore when clearing filters
  };
  // Handle wishlist toggle
  const handleWishlistClick = async (productId: string) => {
    if (!isLoggedIn) {
      showError("Please login to add items to wishlist");
      navigate("/login");
      return;
    }

    if (wishlistLoading[productId]) return;

    setWishlistLoading((prev) => ({ ...prev, [productId]: true }));

    try {
      const isInWishlist = isProductInWishlist(productId);
      if (isInWishlist) {
        await wishlistApi.removeFromWishlist(productId);
        setWishlist((prev) => prev.filter((item) => item !== productId));
        showSuccess("Removed from wishlist");
        notifyWishlistUpdate(); // Notify wishlist drawer to refresh
      } else {
        await wishlistApi.addToWishlist(productId);
        setWishlist((prev) => [...prev, productId]);
        showSuccess("Added to wishlist");
        notifyWishlistUpdate(); // Notify wishlist drawer to refresh
      }
    } catch (error: any) {
      console.error("Wishlist update error:", error);
      showError(error.message || "Failed to update wishlist");
    } finally {
      setWishlistLoading((prev) => ({ ...prev, [productId]: false }));
    }
  };

  const handleAddToCart = async (productId: string) => {
    if (!isLoggedIn) {
      toast.error("Please login to add items to cart");
      navigate("/login");
      return;
    }

    // Check if user already owns this product
    if (ownedProducts.has(productId)) {
      toast.error(
        "You already own this product! Check your previous purchases.",
      );
      navigate("/previous-purchase");
      return;
    }

    try {
      setAddingToCart(productId);

      await cartApi.addToCart({
        productId,
        quantity: 1,
      });
      notifyCartUpdate();
      toast.success("Product added to cart successfully!");
    } catch (error: any) {
      console.error("Error adding to cart:", error);

      if (error?.response?.status === 401) {
        toast.error("Please login to add items to cart");
        navigate("/login");
      } else if (error?.response?.data?.message) {
        // Check if it's an ownership error
        if (error.response.data.message.includes("already own")) {
          toast.error(error.response.data.message);
          setTimeout(() => navigate("/previous-purchase"), 1500);
        } else {
          toast.error(error.response.data.message);
        }
      } else {
        toast.error("Failed to add product to cart. Please try again.");
      }
    } finally {
      setAddingToCart(null);
    }
  };

  // digi packs ===>

  const fetchFeaturedProducts = useCallback(
    async (tabName?: string) => {
      // Prevent overlapping calls
      if (isFetchingFeatured.current) {
        console.log(
          "[Products] Already fetching featured products, skipping...",
        );
        return;
      }

      try {
        setLoadingFeatured(true); // Use separate loading state for featured products
        isFetchingFeatured.current = true;
        const params: IGetProductsParams = {
          page: 1,
          limit: 12,
          sortBy: "createdAt",
          sortOrder: "desc",
        };

        // Only add collection if activeCollection is set
        if (activeCollection) {
          params.collection = activeCollection;
        }

        // Filter by subcategory - use passed tabName or fallback to activeTab state
        const subcategoryToUse = tabName || activeTab;
        if (subcategoryToUse) {
          params.subCategories = subcategoryToUse; // Use subCategories parameter to filter by subcategory name
        }

        console.log(
          "[Products] Fetching featured products with params:",
          params,
          "tabName:",
          subcategoryToUse,
        );
        const response = await productApi.getProducts(params);
        console.log("response.data.products ", response.data.products);
        setFeaturedProducts(response.data.products || []);

        if (isLoggedIn && response.data.products?.length > 0) {
          await checkOwnedProducts(
            response.data.products.map((p: any) => p._id),
          );
        }
      } catch (error) {
        console.error("Error fetching featured products:", error);
      } finally {
        setLoadingFeatured(false); // Use separate loading state for featured products
        isFetchingFeatured.current = false;
      }
    },
    [activeCollection, activeTab, isLoggedIn, checkOwnedProducts],
  );

  // Fetch featured products when activeCollection or activeTab changes
  // Only fetch for Canvas collection to avoid unnecessary API calls
  useEffect(() => {
    // Only fetch featured products if activeCollection is Canvas
    // This prevents unnecessary API calls for DigiVision or when no collection is set
    if (activeCollection === "Canvas") {
      console.log(
        "[Products] Fetching featured products - activeTab:",
        activeTab,
        "activeCollection:",
        activeCollection,
      );
      fetchFeaturedProducts();
    } else if (activeCollection === null && isInitialFeaturedFetch.current) {
      // Reset on initial mount when no collection
      isInitialFeaturedFetch.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCollection, activeTab]); // Depend directly on values, not callback

  // Price range chips (for quick filtering)
  const handlePriceChipClick = (range: string) => {
    setSelectedFilters((prev) => {
      const isSelected = prev.priceRanges?.includes(range);
      return {
        ...prev,
        priceRanges: isSelected
          ? prev.priceRanges.filter((item) => item !== range)
          : [...prev.priceRanges, range],
      };
    });
    setFilters((prev) => ({ ...prev, page: 1 }));
    setHasMore(true);
  };

  const handleBuyNow = async (productId: string) => {
    if (!isLoggedIn) {
      toast.error("Please login to buy this product");
      navigate("/login");
      return;
    }
    if (ownedProducts.has(productId)) {
      toast.error("You already own this product!");
      navigate("/previous-purchase");
      return;
    }
    try {
      setAddingToCart(productId);
      await cartApi.addToCart({ productId, quantity: 1 });

      // Navigate to checkout for immediate purchase
      navigate("/cart");
    } catch (error: any) {
      console.error("Buy Now error:", error);
      toast.error(error?.response?.data?.message || "Failed to buy product");
    } finally {
      setAddingToCart(null);
    }
  };

  // Removed allSubCategories as we're now using static tabs

  const goToPreviousDigiPacks = () => {
    if (digiPacksCarouselRef.current) {
      digiPacksCarouselRef.current.previous();
    }
  };

  const goToNextDigiPacks = () => {
    if (digiPacksCarouselRef.current) {
      digiPacksCarouselRef.current.next();
    }
  };

  const goToPreviousSubcategories = () => {
    if (subcategoriesCarouselRef.current) {
      subcategoriesCarouselRef.current.previous();
    }
  };

  const goToNextSubcategories = () => {
    if (subcategoriesCarouselRef.current) {
      subcategoriesCarouselRef.current.next();
    }
  };

  return (
    <div>
      {/* Page Navigation */}
      <div className="collection_templet_banner">
        <div className="page_navegation_area">
          <div className="container">
            <div className="page_navegation font_two">
              <p>
                <a href="/">Home</a> / <a href="/products?collection=Canvas">Collections</a> /{" "}
                <span className="active fw_700">Painting</span>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Product Collection Section */}
      <section className="product_collection_section" id="product_collection_1">
        <div className="product_collection_sec">
          <div className="container">
            <div className="product_collection_area">
              <div className="row">
                <div className="col-12">
                  <div className="product_collection_detail_area d-flex flex-column">
                    <div className="product_collection_title_box text-sm-center">
                      <div className="d-flex justify-content-between align-items-center w-100">
                        <div className="flex-grow-1 text-sm-center">
                          <h1 className="heading_text_2 mb-1">
                            {activeCollection
                              ? `${activeCollection} Collection`
                              : "All Products"}
                          </h1>
                          <p className="font_two d-sm-block d-none">
                            Transform your place with timeless pieces. Expertly
                            crafted, carefully delivered to your door.
                          </p>
                        </div>
                        {activeCollection &&
                          filteredSubcategories.length > 5 && (
                            <div
                              className="title_arrow_box d-lg-flex d-none"
                              style={{ marginLeft: "auto" }}
                            >
                              <button
                                onClick={goToPreviousSubcategories}
                                className="arrow-btn"
                                aria-label="Previous"
                              >
                                <img
                                  src="/assets/images/nav_arrow_left.png"
                                  alt="Previous"
                                />
                              </button>
                              <button
                                onClick={goToNextSubcategories}
                                className="arrow-btn"
                                aria-label="Next"
                              >
                                <img
                                  src="/assets/images/nav_arrow_right.png"
                                  alt="Next"
                                />
                              </button>
                            </div>
                          )}
                      </div>
                    </div>

                    {activeCollection && activeCollection !== "Canvas" && (
                      <div
                        className="d-flex justify-content-sm-center flex-wrap gap-2 mb-3"
                        style={{ rowGap: "12px", columnGap: "16px" }}
                      >
                        {[
                          "20-100",
                          "101-200",
                          "201-500",
                          "501-1000",
                          "1000",
                        ].map((range) => {
                          const isActive =
                            selectedFilters.priceRanges?.includes(range);
                          return (
                            <button
                              key={range}
                              type="button"
                              onClick={() => handlePriceChipClick(range)}
                              style={{
                                border: "1px solid #112C3B",
                                borderRadius: "9999px",
                                padding: "6px 14px",
                                background: isActive
                                  ? "#112C3B"
                                  : "transparent",
                                color: isActive ? "#FFFFFF" : "#112C3B",
                                fontFamily: "satoshi",
                                fontSize: "14px",
                              }}
                            >
                              {range === "1000"
                                ? "$1001 - above"
                                : `$${range.replace("-", " - ")}`}
                            </button>
                          );
                        })}
                      </div>
                    )}

                    <div className="product_collection_detail_img_box_list">
                      {loadingCategory ? (
                        <div className="text-center py-4 h-50">
                          <div
                            className="spinner-border text-dark"
                            role="status"
                          >
                            <span className="visually-hidden">Loading...</span>
                          </div>
                          <p className="mt-2 font_two">Loading Categories...</p>
                        </div>
                      ) : (
                        <div className="product_collection_detail_img_box font_two">
                          <Carousel
                            ref={subcategoriesCarouselRef}
                            additionalTransfrom={0}
                            arrows={false}
                            autoPlay={true}
                            autoPlaySpeed={3000}
                            infinite
                             customTransition="all 800ms ease-in-out" 
                            responsive={{
                              desktop: {
                                breakpoint: { max: 3000, min: 1024 },
                                items: 5,
                              },
                              tablet: {
                                breakpoint: { max: 1024, min: 600 },
                                items: 3,
                              },
                              mobile: {
                                breakpoint: { max: 600, min: 0 },
                                items: 2,
                              },
                            }}
                            showDots={false}
                            keyBoardControl
                            transitionDuration={500}
                            containerClass="product_collection_carousel"
                            itemClass="product_collection_item"
                          >
                            {(() => {
                              if (!activeCollection) return [];

                              const matchedCategory = categories.find(
                                (category) =>
                                  category.name.split(" ")[0].toLowerCase() ===
                                  activeCollection.toLowerCase(),
                              );

                              return filteredSubcategories.map(
                                (subcatName, index) => {
                                  const subcategoryObj =
                                    matchedCategory?.subCategories.find(
                                      (sub) => sub.name === subcatName,
                                    );

                                  return (
                                    <button
                                      key={index}
                                      type="button"
                                      className="nav_inner_img_box product_collection_info_box"
                                      style={{
                                        background: "none",
                                        border: "none",
                                        padding: 0,
                                        width: "100%",
                                      }}
                                    >
                                      {activeCollection === "Canvas" && (
                                        <div className="product_collection_info_img">
                                          <ImageWithLoader
                                            src={
                                              subcategoryObj?.image ||
                                              `/assets/images/product_collection_info_img_${index + 1}.png`
                                            }
                                            alt={`product_collection_img_${subcatName}`}
                                            activeCollection={activeCollection}
                                            onClick={() => {
                                              if (subcategoryObj?.name) {
                                                setSelectedFilters((prev) => ({
                                                  ...prev,
                                                  subCategories: [
                                                    subcategoryObj.name,
                                                  ],
                                                }));
                                              }
                                            }}
                                          />
                                        </div>
                                      )}

                                      {activeCollection !== "DigiVision" && (
                                        <p className="d-block">{subcatName}</p>
                                      )}
                                    </button>
                                  );
                                },
                              );
                            })()}
                          </Carousel>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                {/* Digi Packs Section */}
                <div>
                  {activeCollection === "Canvas" && (
                    <section className="digi_packs_section">
                      <div className="digi_packs_sec">
                        <div className="container digi_packs_title_area">
                          <div className="digi_packs_area">
                            <div className="row">
                              <div className="col-12">
                                <div className="common_title_area d-flex justify-content-between align-items-center">
                                  <div className="sec_title_box">
                                    <h2 className="heading_text_2 mb-1">
                                      Digi Cartt Picks
                                    </h2>
                                    <p className="font_two">
                                      Hand-selected highlights from our newest
                                      drops, trending artworks, and staff
                                      favorites—updated weekly for fresh
                                      inspiration.
                                    </p>
                                  </div>
                                  <div className="title_arrow_box d-lg-flex d-none">
                                    <button
                                      onClick={goToPreviousDigiPacks}
                                      className="arrow-btn"
                                      aria-label="Previous"
                                    >
                                      <img
                                        src="/assets/images/nav_arrow_left.png"
                                        alt="Previous"
                                      />
                                    </button>
                                    <button
                                      onClick={goToNextDigiPacks}
                                      className="arrow-btn"
                                      aria-label="Next"
                                    >
                                      <img
                                        src="/assets/images/nav_arrow_right.png"
                                        alt="Next"
                                      />
                                    </button>
                                  </div>
                                </div>
                              </div>
                              <div className="col-12">
                                <div className="digi_packs_tabs_area font_two">
                                  <ul className="digi_packs_tabs">
                                    {staticTabs.map((tabName, index) => (
                                      <li
                                        key={index}
                                        className={`tab-nav ${activeTab === tabName ? "active" : ""}`}
                                      >
                                        <button
                                          type="button"
                                          className="tab-nav-item"
                                          onClick={() =>
                                            handleTabClick(tabName)
                                          }
                                        >
                                          {tabName}
                                        </button>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              </div>
                              {/* Carousel Section */}
                              <div className="col-12">
                                {loadingFeatured ? (
                                  <div className="text-center py-4 h-50">
                                    <div
                                      className="spinner-border text-dark"
                                      role="status"
                                    >
                                      <span className="visually-hidden">
                                        Loading ...
                                      </span>
                                    </div>
                                    <p className="mt-2 font_two">Loading ...</p>
                                  </div>
                                ) : (
                                  <Carousel
                                    ref={digiPacksCarouselRef}
                                    additionalTransfrom={0}
                                    arrows={false}
                                    autoPlay={true}
                                    autoPlaySpeed={4000}
                                    infinite
                                    responsive={{
                                      desktop: {
                                        breakpoint: { max: 3000, min: 1024 },
                                        items: 3,
                                        slidesToSlide: 1,
                                      },
                                      tablet: {
                                        breakpoint: { max: 1024, min: 464 },
                                        items: 2,
                                        slidesToSlide: 1,
                                      },
                                      mobile: {
                                        breakpoint: { max: 464, min: 0 },
                                        items: 1,
                                        slidesToSlide: 1,
                                      },
                                    }}
                                    showDots={false}
                                    keyBoardControl
                                    customTransition="transform 500ms ease-in-out"
                                    transitionDuration={500}
                                    containerClass="digi_packs_content_list"
                                    itemClass="col-lg-4 col-sm-6 digi_packs_carousel_item"
                                  >
                                    {featuredProducts.map((product) => (
                                      <div
                                        key={product._id}
                                        className="most_selling_art_box digi_packs_box d-flex flex-column"
                                      >
                                        <div className="most_selling_art_box_img position-relative">
                                          {isLoggedIn && (
                                            <button
                                              type="button"
                                              className="like_icon"
                                              onClick={(e) => {
                                                e.stopPropagation(); // Prevent event bubbling
                                                handleWishlistClick(
                                                  product?._id,
                                                );
                                              }}
                                              style={{
                                                background: "none",
                                                border: "none",
                                                padding: 0,
                                                zIndex:1,
                                              }}
                                            >
                                              <svg
                                                width="46"
                                                height="46"
                                                viewBox="0 0 46 46"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                              >
                                                <rect
                                                  width="46"
                                                  height="46"
                                                  rx="23"
                                                  fill="#FAF6EE"
                                                />
                                                <path
                                                  d="M29.8569 14.7447C25.8652 12.6038 22.9991 15.7597 22.9991 15.7597C22.9991 15.7597 20.1328 12.6038 16.1411 14.7447C11.3064 17.3377 10.9592 27.492 22.9991 32C35.0389 27.492 34.6916 17.3377 29.8569 14.7447Z"
                                                  stroke="#112C3B"
                                                  strokeWidth="1.5"
                                                  strokeLinecap="round"
                                                  fill={
                                                    isProductInWishlist(
                                                      product._id,
                                                    )
                                                      ? "#112C3B"
                                                      : "none"
                                                  }
                                                />
                                              </svg>
                                            </button>
                                          )}

                                          <ImageWithLoader
                                            src={
                                              product.image ||
                                              "/assets/images/nav_img.png"
                                            }
                                            alt={product.name}
                                            className="w-100"
                                            style={{
                                              width: "460px",
                                              height: "250px",
                                              objectFit: "cover",
                                            }}
                                            activeCollection = {activeCollection}
                                            onClick={() =>
                                              navigate(
                                               `/product/${product._id}${
  activeCollection === "Canvas" ? "?collection=Canvas" : ""
}`,
                                              )
                                            }
                                          />

                                          <div className="common_multi_btn_area">
                                            {!ownedProducts.has(
                                              product._id,
                                            ) && (
                                              <button
                                                type="button"
                                                className="blue_btn light_btn"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleAddToCart(product._id);
                                                }}
                                              >
                                                {addingToCart === product._id
                                                  ? "Adding..."
                                                  : "Add to Cart"}
                                              </button>
                                            )}
                                            {ownedProducts.has(product._id) ? (
                                              <button
                                                type="button"
                                                className="blue_btn"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  navigate(
                                                    "/previous-purchase",
                                                  );
                                                }}
                                                style={{
                                                  background: "#10B981",
                                                  borderColor: "#10B981",
                                                }}
                                              >
                                                ✓ Owned
                                              </button>
                                            ) : (
                                              <button
                                                type="button"
                                                className="blue_btn"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleBuyNow(product._id);
                                                }}
                                              >
                                                {addingToCart === product._id
                                                  ? "Processing..."
                                                  : "Buy Now"}
                                              </button>
                                            )}
                                          </div>
                                        </div>

                                        <div className="nav_inner_details_box_info font_two color_darkblue">
                                          <p className="fw_500">
                                            {product.name}
                                          </p>
                                          <p className="nav_inner_details_info fw_500">
                                            {product.artistId.name},{" "}
                                            {product?.artistId?.country}
                                          </p>
                                          <div className="nav_inner_details_price_box">
                                            <span className="nav_box_price fw_700">
                                              ${product.price}
                                            </span>
                                            <span className="nav_box_cm fw_500">
                                              {convertInchesToCm(
                                                product?.fileDetails
                                                  .recommendedPrintSize,
                                              )}
                                            </span>
                                            {/*{product.fileDetails?.recommendedPrintSize && (*/}
                                            {/*  <span className="nav_box_cm fw_500">*/}
                                            {/*    {product.fileDetails.recommendedPrintSize}*/}
                                            {/*  </span>*/}
                                            {/*)}*/}
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </Carousel>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </section>
                  )}
                </div>
                <div className="col-12">
                  <div className="product_collection_part">
                    <div className="product_collection_filter_area d-sm-block">
                      <div className="product_collection_filter_area_inside font_two d-flex justify-content-between align-items-center flex-wrap">
                        <div className="collection_filler_box font_two d-flex align-items-start">
                          <p className="collection_filler_heading">
                            <span>Filters:</span>
                            {/* {Object.values(selectedFilters).flat().length > 0 && (
                              <span className="ms-2 text-primary">
                                ({Object.values(selectedFilters).flat().length} applied)
                              </span>
                            )} */}
                            {Object.values(selectedFilters).flat().length >
                              0 && (
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary ms-2"
                                onClick={clearAllFilters}
                              >
                                Clear All
                              </button>
                            )}
                          </p>
                          <ul className="collection_filler_list_menu d-flex flex-wrap">
                            {activeCollection === "Canvas" && (
                              <li className="filler-button-dropdown dropdown">
                                <button
                                  type="button"
                                  className="filler-button dropdown-toggle"
                                  data-bs-toggle="dropdown"
                                  aria-expanded="false"
                                  data-bs-auto-close="outside"
                                >
                                  Category
                                  {selectedFilters.subCategories?.length >
                                    0 && (
                                    <span className="ms-1 badge bg-dark">
                                      {selectedFilters.subCategories.length}
                                    </span>
                                  )}
                                </button>
                                <div className="filler_menu dropdown-menu">
                                  <p className="fw_700">Categories</p>
                                  <div className="filler_menu_inner">
                                    {/* "All" option */}
                                    <div className="form-check">
                                      <input
                                        type="checkbox"
                                        className="form-check-input"
                                        id="dropdownAll"
                                        checked={
                                          selectedFilters.subCategories
                                            ?.length === 0
                                        }
                                        onChange={(e) => {
                                          if (e.target.checked) {
                                            setSelectedFilters((prev) => ({
                                              ...prev,
                                              subCategories: [],
                                            }));
                                          }
                                        }}
                                      />
                                      <label
                                        className="form-check-label"
                                        htmlFor="dropdownAll"
                                      >
                                        All Categories
                                      </label>
                                    </div>

                                    {/* Dynamic subcategories */}
                                    {loadingCategory ? (
                                      <div className="d-flex justify-content-center align-items-center py-3">
                                        <Loader size="small" color="#E7D5AD" />
                                      </div>
                                    ) : (
                                      filteredSubcategories.map(
                                        (subcategoryName, index) => {
                                          const id = `dropdown-${subcategoryName?.replace(/\s+/g, "")}`;
                                          return (
                                            <div
                                              className="form-check"
                                              key={index}
                                            >
                                              <input
                                                type="checkbox"
                                                className="form-check-input"
                                                id={id}
                                                checked={
                                                  selectedFilters.subCategories?.includes(
                                                    subcategoryName,
                                                  ) || false
                                                }
                                                onChange={(e) =>
                                                  handleFilterChange(
                                                    "subCategories",
                                                    subcategoryName,
                                                    e.target.checked,
                                                  )
                                                }
                                              />
                                              <label
                                                className="form-check-label"
                                                htmlFor={id}
                                              >
                                                {subcategoryName}
                                              </label>
                                            </div>
                                          );
                                        },
                                      )
                                    )}

                                    {selectedFilters.subCategories?.length >
                                      0 && (
                                      <button
                                        type="button"
                                        className="link_btn"
                                        style={{
                                          background: "none",
                                          border: "none",
                                          color: "#dc3545",
                                        }}
                                        onClick={() => {
                                          setSelectedFilters((prev) => ({
                                            ...prev,
                                            subCategories: [],
                                          }));
                                        }}
                                      >
                                        Clear All
                                      </button>
                                    )}
                                    <button
                                      type="button"
                                      className="link_btn"
                                      style={{
                                        background: "none",
                                        border: "none",
                                      }}
                                    >
                                      Show More
                                    </button>
                                  </div>
                                </div>
                              </li>
                            )}

                            <li className="filler-button-dropdown dropdown">
                              <button
                                type="button"
                                className="filler-button dropdown-toggle"
                                data-bs-toggle="dropdown"
                                aria-expanded="false"
                                data-bs-auto-close="outside"
                              >
                                Style
                                {selectedFilters.styles?.length > 0 && (
                                  <span className="ms-1 badge bg-dark">
                                    {selectedFilters.styles.length}
                                  </span>
                                )}
                              </button>
                              <div className="filler_menu dropdown-menu">
                                <p className="fw_700">Style</p>
                                <div className="filler_menu_inner">
                                  {loadingFilters ? (
                                    <div className="d-flex justify-content-center align-items-center py-3">
                                      <Loader size="small" color="#E7D5AD" />
                                    </div>
                                  ) : (
                                    Array.isArray(styles) &&
                                    styles.map((style) => (
                                      <div
                                        key={style._id}
                                        className="form-check"
                                      >
                                        <input
                                          type="checkbox"
                                          className="form-check-input"
                                          id={`style-${style._id}`}
                                          checked={
                                            selectedFilters.styles?.includes(
                                              style.name,
                                            ) || false
                                          }
                                          onChange={(e) =>
                                            handleFilterChange(
                                              "styles",
                                              style.name,
                                              e.target.checked,
                                            )
                                          }
                                        />
                                        <label
                                          className="form-check-label"
                                          htmlFor={`style-${style._id}`}
                                        >
                                          {style.name}
                                        </label>
                                      </div>
                                    ))
                                  )}
                                </div>
                              </div>
                            </li>
                            <li className="filler-button-dropdown dropdown">
                              <button
                                type="button"
                                className="filler-button dropdown-toggle"
                                data-bs-toggle="dropdown"
                                aria-expanded="false"
                                data-bs-auto-close="outside"
                              >
                                Material
                                {selectedFilters.materials?.length > 0 && (
                                  <span className="ms-1 badge bg-dark">
                                    {selectedFilters.materials.length}
                                  </span>
                                )}
                              </button>
                              <div className="filler_menu dropdown-menu">
                                <p className="fw_700">Material</p>
                                <div className="filler_menu_inner">
                                  {loadingFilters ? (
                                    <div className="d-flex justify-content-center align-items-center py-3">
                                      <Loader size="small" color="#E7D5AD" />
                                    </div>
                                  ) : (
                                    Array.isArray(materials) &&
                                    materials.map((material) => (
                                      <div
                                        key={material._id}
                                        className="form-check"
                                      >
                                        <input
                                          type="checkbox"
                                          className="form-check-input"
                                          id={`material-${material._id}`}
                                          checked={
                                            selectedFilters.materials?.includes(
                                              material.name,
                                            ) || false
                                          }
                                          onChange={(e) =>
                                            handleFilterChange(
                                              "materials",
                                              material.name,
                                              e.target.checked,
                                            )
                                          }
                                        />
                                        <label
                                          className="form-check-label"
                                          htmlFor={`material-${material._id}`}
                                        >
                                          {material.name}
                                        </label>
                                      </div>
                                    ))
                                  )}
                                </div>
                              </div>
                            </li>
                            <li className="filler-button-dropdown dropdown">
                              <button
                                type="button"
                                className="filler-button dropdown-toggle"
                                data-bs-toggle="dropdown"
                                aria-expanded="false"
                                data-bs-auto-close="outside"
                              >
                                Color
                                {selectedFilters.colors?.length > 0 && (
                                  <span className="ms-1 badge bg-dark">
                                    {selectedFilters.colors.length}
                                  </span>
                                )}
                              </button>
                              <div className="filler_menu dropdown-menu">
                                <p className="fw_700">Color</p>
                                <div className="filler_menu_inner">
                                  {loadingFilters ? (
                                    <div className="d-flex justify-content-center align-items-center py-3">
                                      <Loader size="small" color="#E7D5AD" />
                                    </div>
                                  ) : (
                                    Array.isArray(colors) &&
                                    colors.map((color) => (
                                      <div
                                        key={color._id}
                                        className="form-check"
                                      >
                                        <input
                                          type="checkbox"
                                          className="form-check-input"
                                          id={`color-${color._id}`}
                                          checked={
                                            selectedFilters.colors?.includes(
                                              color.name,
                                            ) || false
                                          }
                                          onChange={(e) =>
                                            handleFilterChange(
                                              "colors",
                                              color.name,
                                              e.target.checked,
                                            )
                                          }
                                        />
                                        <label
                                          className="form-check-label"
                                          htmlFor={`color-${color._id}`}
                                        >
                                          {color.name}
                                        </label>
                                      </div>
                                    ))
                                  )}
                                </div>
                              </div>
                            </li>
                            <li className="filler-button-dropdown dropdown">
                              <button
                                type="button"
                                className="filler-button dropdown-toggle"
                                data-bs-toggle="dropdown"
                                aria-expanded="false"
                                data-bs-auto-close="outside"
                              >
                                Price
                                {selectedFilters.priceRanges?.length > 0 && (
                                  <span className="ms-1 badge bg-dark">
                                    {selectedFilters.priceRanges.length}
                                  </span>
                                )}
                              </button>
                              <div className="filler_menu dropdown-menu">
                                <p className="fw_700">Price Range</p>
                                <div className="filler_menu_inner">
                                  <div className="form-check">
                                    <input
                                      type="checkbox"
                                      className="form-check-input"
                                      id="price-20-100"
                                      checked={
                                        selectedFilters.priceRanges?.includes(
                                          "20-100",
                                        ) || false
                                      }
                                      onChange={(e) =>
                                        handlePriceRangeChange(
                                          "20-100",
                                          e.target.checked,
                                        )
                                      }
                                    />
                                    <label
                                      className="form-check-label"
                                      htmlFor="price-20-100"
                                    >
                                      $20 - $100
                                    </label>
                                  </div>
                                  <div className="form-check">
                                    <input
                                      type="checkbox"
                                      className="form-check-input"
                                      id="price-101-200"
                                      checked={
                                        selectedFilters.priceRanges?.includes(
                                          "101-200",
                                        ) || false
                                      }
                                      onChange={(e) =>
                                        handlePriceRangeChange(
                                          "101-200",
                                          e.target.checked,
                                        )
                                      }
                                    />
                                    <label
                                      className="form-check-label"
                                      htmlFor="price-101-200"
                                    >
                                      $101 - $200
                                    </label>
                                  </div>
                                  <div className="form-check">
                                    <input
                                      type="checkbox"
                                      className="form-check-input"
                                      id="price-201-500"
                                      checked={
                                        selectedFilters.priceRanges?.includes(
                                          "201-500",
                                        ) || false
                                      }
                                      onChange={(e) =>
                                        handlePriceRangeChange(
                                          "201-500",
                                          e.target.checked,
                                        )
                                      }
                                    />
                                    <label
                                      className="form-check-label"
                                      htmlFor="price-201-500"
                                    >
                                      $201 - $500
                                    </label>
                                  </div>
                                  <div className="form-check">
                                    <input
                                      type="checkbox"
                                      className="form-check-input"
                                      id="price-501-1000"
                                      checked={
                                        selectedFilters.priceRanges?.includes(
                                          "501-1000",
                                        ) || false
                                      }
                                      onChange={(e) =>
                                        handlePriceRangeChange(
                                          "501-1000",
                                          e.target.checked,
                                        )
                                      }
                                    />
                                    <label
                                      className="form-check-label"
                                      htmlFor="price-501-1000"
                                    >
                                      $501 - $1000
                                    </label>
                                  </div>
                                  <div className="form-check">
                                    <input
                                      type="checkbox"
                                      className="form-check-input"
                                      id="price-1000-plus"
                                      checked={
                                        selectedFilters.priceRanges?.includes(
                                          "1000+",
                                        ) || false
                                      }
                                      onChange={(e) =>
                                        handlePriceRangeChange(
                                          "1000+",
                                          e.target.checked,
                                        )
                                      }
                                    />
                                    <label
                                      className="form-check-label"
                                      htmlFor="price-1000-plus"
                                    >
                                      $1000 and above!
                                    </label>
                                  </div>
                                </div>
                              </div>
                            </li>
                            <li className="filler-button-dropdown dropdown">
                              <button
                                type="button"
                                className="filler-button dropdown-toggle"
                                data-bs-toggle="dropdown"
                                aria-expanded="false"
                                data-bs-auto-close="outside"
                              >
                                Artist
                                {selectedFilters.artists?.length > 0 && (
                                  <span className="ms-1 badge bg-dark">
                                    {selectedFilters.artists.length}
                                  </span>
                                )}
                              </button>
                              <div className="filler_menu dropdown-menu">
                                <p className="fw_700">Artist</p>
                                <div className="filler_menu_inner">
                                  {loadingFilters ? (
                                    <div className="d-flex justify-content-center align-items-center py-3">
                                      <Loader size="small" color="#E7D5AD" />
                                    </div>
                                  ) : (
                                    Array.isArray(artists) &&
                                    artists.map((artist) => (
                                      <div
                                        key={artist._id}
                                        className="form-check"
                                      >
                                        <input
                                          type="checkbox"
                                          className="form-check-input"
                                          id={`artist-${artist._id}`}
                                          checked={
                                            selectedFilters.artists?.includes(
                                              artist.name,
                                            ) || false
                                          }
                                          onChange={(e) =>
                                            handleFilterChange(
                                              "artists",
                                              artist.name,
                                              e.target.checked,
                                            )
                                          }
                                        />
                                        <label
                                          className="form-check-label"
                                          htmlFor={`artist-${artist._id}`}
                                        >
                                          {artist.name}
                                        </label>
                                      </div>
                                    ))
                                  )}
                                </div>
                              </div>
                            </li>
                            {/* <li className="filler-button-dropdown dropdown">
                              <button
                                type="button"
                                className="filler-button dropdown-toggle"
                                data-bs-toggle="dropdown"
                                aria-expanded="false"
                                data-bs-auto-close="outside"
                              >
                                Material
                              </button>
                              <div className="filler_menu dropdown-menu">
                                <p className="fw_700">Material</p>
                                <div className="filler_menu_inner">
                                  {loadingFilters ? (
                                    <div className="d-flex justify-content-center align-items-center py-3">
                                      <Loader size="small" color="#E7D5AD" />
                                    </div>
                                  ) : (
                                    Array.isArray(materials) && materials.map((material) => (
                                      <div key={material._id} className="form-check">
                                        <input
                                          type="checkbox"
                                          className="form-check-input"
                                          id={`material-${material._id}`}
                                          checked={selectedFilters.materials?.includes(material.name) || false}
                                          onChange={(e) => handleFilterChange('materials', material.name, e.target.checked)}
                                        />
                                        <label
                                          className="form-check-label"
                                          htmlFor={`material-${material._id}`}
                                        >
                                          {material.name}
                                        </label>
                                      </div>
                                    ))
                                  )}
                                </div>
                              </div>
                            </li>
                            <li className="filler-button-dropdown dropdown">
                              <button
                                type="button"
                                className="filler-button dropdown-toggle"
                                data-bs-toggle="dropdown"
                                aria-expanded="false"
                                data-bs-auto-close="outside"
                              >
                                Color
                              </button>
                              <div className="filler_menu dropdown-menu">
                                <p className="fw_700">Color</p>
                                <div className="filler_menu_inner">
                                  {loadingFilters ? (
                                    <div className="d-flex justify-content-center align-items-center py-3">
                                      <Loader size="small" color="#E7D5AD" />
                                    </div>
                                  ) : (
                                    Array.isArray(colors) && colors.map((color) => (
                                      <div key={color._id} className="form-check">
                                        <input
                                          type="checkbox"
                                          className="form-check-input"
                                          id={`color-${color._id}`}
                                          checked={selectedFilters.colors?.includes(color.name) || false}
                                          onChange={(e) => handleFilterChange('colors', color.name, e.target.checked)}
                                        />
                                        <label
                                          className="form-check-label"
                                          htmlFor={`color-${color._id}`}
                                        >
                                          {color.name}
                                        </label>
                                      </div>
                                    ))
                                  )}
                                </div>
                              </div>
                            </li> */}
                          </ul>
                        </div>
                        <div className="collection_sort_box font_two d-flex align-items-center">
                          <p className="collection_sort_heading">
                            <span>Sort by:</span>
                          </p>
                          <div className="collection_sort_select_box">
                            <select
                              style={{
                                border: "none",
                                background: "none",
                                padding: "0px 5px",
                              }}
                              className="collection_sort_select"
                              value={`${filters.sortBy}-${filters.sortOrder}`}
                              onChange={(e) => {
                                const [sortBy, sortOrder] =
                                  e.target.value.split("-");
                                setFilters((prev) => ({
                                  ...prev,
                                  sortBy: sortBy as any,
                                  sortOrder: sortOrder as any,
                                  page: 1,
                                }));
                              }}
                            >
                              <option value="createdAt-desc">
                                Newest First
                              </option>
                              <option value="createdAt-asc">
                                Oldest First
                              </option>
                              <option value="price-asc">
                                Price: Low to High
                              </option>
                              <option value="price-desc">
                                Price: High to Low
                              </option>
                              <option value="name-asc">Name: A to Z</option>
                              <option value="name-desc">Name: Z to A</option>
                              <option value="popularity-desc">
                                Most Popular
                              </option>
                            </select>
                          </div>
                        </div>
                      </div>
                      <hr className="cutom_filter_hr" />
                      <div className="product_collection_filters_list font_two d-flex align-items-center">
                        <ul className="product_collection_filters_tags d-flex">
                          {Object.entries(selectedFilters).flatMap(
                            ([filterType, filterValues]) =>
                              filterValues.map((value, index) => {
                                return (
                                  <li
                                    key={`${filterType}-${index}`}
                                    style={{
                                      border: "1px solid",
                                      borderRadius: "20px",
                                      padding: "5px 10px",
                                    }}
                                  >
                                    <button
                                      type="button"
                                      onClick={() => {
                                        // Remove this filter
                                        setSelectedFilters((prev) => ({
                                          ...prev,
                                          [filterType]: prev[
                                            filterType as keyof typeof prev
                                          ].filter((v) => v !== value),
                                        }));
                                      }}
                                      style={{
                                        background: "none",
                                        border: "none",
                                        padding: 0,
                                      }}
                                    >
                                      {/* $ {value} */}
                                      {filterType === "priceRanges"
                                        ? `$ ${value}`
                                        : value}
                                      <span className="filter_close">
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          width="18"
                                          height="18"
                                          viewBox="0 0 18 18"
                                          fill="none"
                                        >
                                          <path
                                            d="M14.25 3.74994L3.75 14.2499"
                                            stroke="#141B34"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                          />
                                          <path
                                            d="M3.75 3.74994L14.25 14.2499"
                                            stroke="#141B34"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                          />
                                        </svg>
                                      </span>
                                    </button>
                                  </li>
                                );
                              }),
                          )}
                        </ul>
                        {/* <button type="button" className="cleasr_all_btn link_btn" style={{ background: 'none', border: 'none' }} onClick={clearAllFilters}>
                          Remove All
                        </button> */}
                      </div>
                    </div>
                    <div className="product_collection_grid_list_area">
                      {loading ? (
                        <div className="container py-5 text-center h-50">
                          <div className="spinner-border" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                          <p className="mt-3">Loading product details...</p>
                        </div>
                      ) : (
                        <div className="row">
                          {(products || []).map((product, index) => (
                            <div
                              key={product._id}
                              className="col-xl-3 col-lg-4 col-sm-6 col-12"
                              style={{ paddingBottom: "20px" }}
                            >
                              <div className="product_collection_grid_list d-sm-flex">
                                <div className="product_collection_grid_item">
                                  <div className="product_collection_grid_box">
                                    <div className="product_collection_grid_box_img position-relative overflow-hidden">
                                      {
                                        <button
                                          type="button"
                                          className="like_icon"
                                          onClick={(e) => {
                                            e.stopPropagation(); // Prevent event bubbling
                                            handleWishlistClick(product?._id);
                                          }}
                                          style={{
                                            background: "none",
                                            border: "none",
                                            padding: 0,
                                              zIndex:1,
                                          }}
                                        >
                                          <svg
                                            width="46"
                                            height="46"
                                            viewBox="0 0 46 46"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                          >
                                            <rect
                                              width="46"
                                              height="46"
                                              rx="23"
                                              fill="#FAF6EE"
                                            />
                                            <path
                                              d="M29.8569 14.7447C25.8652 12.6038 22.9991 15.7597 22.9991 15.7597C22.9991 15.7597 20.1328 12.6038 16.1411 14.7447C11.3064 17.3377 10.9592 27.492 22.9991 32C35.0389 27.492 34.6916 17.3377 29.8569 14.7447Z"
                                              stroke="#112C3B"
                                              strokeWidth="1.5"
                                              strokeLinecap="round"
                                              fill={
                                                isProductInWishlist(product._id)
                                                  ? "#112C3B"
                                                  : "none"
                                              }
                                            />
                                          </svg>
                                        </button>
                                      }

                                      {/* Product Image */}
                                      <ImageWithLoader
                                        src={
                                          product.image ||
                                          `/assets/images/product_grid_img_responsive_${index + 1}.png`
                                        }
                                        style={{
                                          cursor: "pointer",
                                        }}
                                        alt={product.name}
                                        activeCollection={activeCollection}
                                        onClick={() =>
                                          navigate(`/product/${product._id}${
  activeCollection === "Canvas" ? "?collection=Canvas" : ""
}`)
                                        }
                                      />

                                      <div className="common_multi_btn_area">
                                        {ownedProducts.has(product._id) ? (
                                          <button
                                            type="button"
                                            className="blue_btn"
                                            onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              navigate("/previous-purchase");
                                            }}
                                            style={{
                                              background: "#10B981",
                                              borderColor: "#10B981",
                                            }}
                                          >
                                            ✓ Owned
                                          </button>
                                        ) : (
                                          <>
                                            <button
                                              type="button"
                                              className="blue_btn light_btn"
                                              onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                handleAddToCart(product._id);
                                              }}
                                              disabled={
                                                addingToCart === product._id
                                              }
                                            >
                                              {addingToCart === product._id
                                                ? "Adding..."
                                                : "Add to Cart"}
                                            </button>
                                            <Link
                                            to={`/product/${product._id}${
  activeCollection === "Canvas" ? "?collection=Canvas" : ""
}`}

                                              className="blue_btn"
                                            >
                                              Buy Now
                                            </Link>
                                          </>
                                        )}
                                      </div>
                                    </div>

                                    <div className="nav_inner_details_box_info font_two color_darkblue">
                                      <p className="nav_inner_details_box_heading fw_500">
                                        {product.name}
                                      </p>
                                      <p className="nav_inner_details_info fw_500">
                                        {product.artistId.name},{" "}
                                        {product?.artistId?.country}
                                      </p>
                                      <div className="nav_inner_details_price_box">
                                        <span className="nav_box_price fw_700">
                                          ${product.price.toFixed(2)}
                                        </span>
                                        <span className="nav_box_cm fw_500">
                                          {convertInchesToCm(
                                            product?.fileDetails
                                              .recommendedPrintSize,
                                          )}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div
                      className="product_collection_pagination_area text-center"
                      style={{
                        display: "flex",
                        justifyContent: "center",
                      }}
                    >
                      {hasMore && (
                        <div
                          onClick={loadMoreProducts}
                          style={{
                            backgroundColor: "transparent",
                            cursor: "pointer",
                            color: "#112C3B",
                            fontSize: "18px",
                            fontWeight: "400",
                            minWidth: "140px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "8px",
                            fontFamily: "satoshi",
                          }}
                        >
                          {loadingMore ? (
                            <>
                              <div
                                className="spinner-border spinner-border-sm"
                                role="status"
                                style={{ width: "16px", height: "16px" }}
                              >
                                <span className="visually-hidden">
                                  Loading...
                                </span>
                              </div>
                              Loading...
                            </>
                          ) : (
                            <>
                              Load More
                              <svg
                                width="20"
                                height="20"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M6 9L12 15L18 9"
                                  stroke="#112C3B"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Products;
